/**
 * Stałe kody rabatowe zależne od WARTOŚCI zamówienia („Im więcej kupujesz, tym
 * więcej zyskujesz"). Kody są inne per sklep — lavmag = EXTRA*, youlook = MORE* —
 * więc listę, progi i kolory trzyma konfiguracja marki (app/brands/<brand>/config.ts).
 * Tutaj tylko logika czysta, testowalna bez Nuxta.
 *
 * UWAGA: kody MUSZĄ istnieć jako reguły koszyka (cart rules) w PrestaShop danego
 * sklepu — provisionuje je komenda `contelizer:promo:standing-codes` w backendzie.
 */

export interface StandingPromoCode {
  /** Kod kuponu = `code` reguły koszyka w PrestaShop */
  code: string
  /** Etykieta rabatu do UI, np. „-10%” */
  pct: string
  /** Próg wartości zamówienia do UI, np. „100 PLN” */
  from: string
}

export interface StandingPromoConfig {
  /**
   * Blok kodów nie renderuje się przed tą datą (ISO 8601 ze strefą).
   * null = akcja jest aktywna od razu po wdrożeniu.
   */
  startsAt: string | null
  /** Kolory panelu kodów. Belka u góry i popup mają własne kolory — dane z PrestaShop. */
  accent: {
    /** Tło panelu */
    bg: string
    /** Kolor tekstu na tle panelu (musi mieć kontrast do `bg`) */
    text: string
    /** Wyróżnienie najwyższego progu */
    best: string
  }
  /** Kolejność = kolejność w UI; ostatni próg jest wyróżniany jako najlepszy. */
  codes: StandingPromoCode[]
}

/** „EXTRA3” → „EXTRA”, „MORE10” → „MORE”; kod bez cyfry → cały kod. */
function codeFamily(code: string): string {
  const match = /^([A-Za-z]+)\d/.exec(code.trim())
  return (match?.[1] ?? code.trim()).toUpperCase()
}

/** Rodziny kodów (prefiksy literowe) występujące w konfiguracji marki. */
export function standingPromoFamilies(codes: string[]): string[] {
  return [...new Set(codes.map(codeFamily).filter(Boolean))]
}

/**
 * Czy kod należy do akcji stałych kodów danej marki.
 *
 * Poza dokładnym trafieniem w listę łapiemy też CAŁĄ rodzinę (np. każdy `MORE<cyfra>`),
 * bo w PrestaShop mogą istnieć kody progowe nie pokazywane w UI (stare/testowe), a na
 * karcie produktu wszystkie muszą być wyłączone z bloku „promo notices” — ich data
 * końca jest odległa i licznik pokazywałby bzdurę w rodzaju „1824d”.
 */
export function isStandingPromoCode(
  code: string | null | undefined,
  codes: string[],
): boolean {
  if (!code) return false
  const normalized = code.trim().toUpperCase()
  if (!normalized) return false
  if (codes.some(c => c.trim().toUpperCase() === normalized)) return true
  return standingPromoFamilies(codes).some(family =>
    new RegExp(`^${family}\\d`).test(normalized),
  )
}
